import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Spinner,
  Modal,
  Pagination
} from "react-bootstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useAccounts } from "../../../contextjs/AccountContext";
import { useLedgerReports } from "../../../contextjs/LedgerReportsContext";
import { formatNairobiDateTime } from "../../../utils/dateTime";
import { exportLedgerPdf } from "../../../api/api";
import { AccountingScopeSelect, useAccountingScope } from "../accountingscope";

// retrieve account object to see its account_type to format balance as either Dr or Cr
function formatDrCr(balance, accountTypeName) {
  const debitSideTypes = ["Asset", "Expense", "Receivable", "Contra Revenue"];
  const isDebitType = debitSideTypes.includes(accountTypeName);

  //if a debit normal type:
  if (isDebitType) {
      return balance >= 0
          ? `Dr ${balance.toFixed(2)}`
          : `Cr ${Math.abs(balance).toFixed(2)}`;
  } else {
      return balance >= 0
          ? `Cr ${balance.toFixed(2)}`
          : `Dr ${Math.abs(balance).toFixed(2)}`;
  }
}

const LedgerReports = ({ accountId }) => {
  const navigate = useNavigate();

  const { accounts, loading: accountsLoading } = useAccounts();
  const { ledgerTransactions, totalCount, openingBalanceForPage, loading: ledgerLoading, loadLedger } = useLedgerReports();
  const scope = useAccountingScope();

  // local date/page
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [exportingPdf, setExportingPdf] = useState(false);

  const entriesPerPage = 20;
  const totalPages = Math.ceil(totalCount / entriesPerPage);

  // chosen account
  const [selectedAccount, setSelectedAccount] = useState(null);

  // details modal
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    if (accountId && accounts.length > 0) {
      const acc = accounts.find(ac => ac.id === accountId);
      if (acc) {
        setSelectedAccount({ value: acc.id, label: acc.account_name });
      }
    }
  }, [accountId, accounts]);

  // 1) On changes to page/dates/account => load from server
  useEffect(() => {
    if (!selectedAccount) return; // skip if no account chosen

    const filters = {
        start_date: startDate,
        end_date: endDate,
        account_id: selectedAccount.value,
        store_id: scope.storeId,
    };

    loadLedger(currentPage, entriesPerPage, filters);
  }, [selectedAccount, startDate, endDate, currentPage, loadLedger, scope.storeId]);

  // after fetching accounts and selectedAccount
  const chosenAccObj = accounts.find(ac => ac.id === selectedAccount?.value);
  const accountTypeName = chosenAccObj?.account_type_details?.account_type_name
      || chosenAccObj?.account_type?.account_type_name || "";

  // calculate running balance
  const isDebitType = ["Asset", "Expense", "Receivable", "Contra Revenue"].includes(accountTypeName);
  let runningBalance = parseFloat(openingBalanceForPage || 0);

  const processedTransactions = ledgerTransactions.map(transactions => {
    const accountItems = transactions.items.filter(
      (item) => item.account === selectedAccount?.value);

    const totalDebit = accountItems
        .filter(item => item.transaction_type === "DEBIT")
        .reduce((sum, item) => sum + parseFloat(item.amount), 0);

    const totalCredit = accountItems
        .filter(item => item.transaction_type === "CREDIT")
        .reduce((sum, item) => sum + parseFloat(item.amount), 0);

    // adjust running balance depending on account type
    if (isDebitType) {
        runningBalance += totalDebit - totalCredit;
    } else {
        runningBalance += totalCredit - totalDebit;
    }

    return {
        ...transactions,
        totalDebit,
        totalCredit,
        balance: runningBalance
    };
  });

  // handle date changes => reset page=1
  const handleDateChange = (which, val) => {
    setCurrentPage(1);
    if (which === "start") setStartDate(val);
    if (which === "end")   setEndDate(val);
  };

  // handle account change => reset page=1
  const handleAccountChange = (option) => {
    setSelectedAccount(option);
    setCurrentPage(1);
  };

  // pagination
  const handlePageChange = (newPage) => {
    if (newPage !== currentPage && newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleShowDetails = (trx) => {
    setSelectedTransaction(trx);
    setShowDetails(true);
  };
  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedTransaction(null);
  };

  const downloadBlob = (response, fallbackName) => {
    const disposition = response.headers?.["content-disposition"];
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] || fallbackName;
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = async (exportScope = "filtered") => {
    if (!selectedAccount) return;
    setExportingPdf(true);
    try {
      const filters = {
        account_id: selectedAccount.value,
        scope: exportScope,
        store_id: scope.storeId,
      };

      if (exportScope === "filtered") {
        filters.start_date = startDate;
        filters.end_date = endDate;
      }

      const response = await exportLedgerPdf(filters);
      downloadBlob(response, `ledger_${selectedAccount.label}.pdf`);
    } catch (error) {
      console.error("Failed to export ledger PDF:", error);
      alert("Failed to export ledger PDF. Please try again.");
    } finally {
      setExportingPdf(false);
    }
  };

  if (accountsLoading || ledgerLoading || scope.loadingScope) {
    return <Spinner animation="border" />;
  }

  const accountOptions = (accounts).map(ac => ({
    value: ac.id,
    label: ac.account_name
  }));

  const handleBack = () => navigate(-1);

    // Bounded pagination range for display
  const maxLinks = 5;
  let startLink = Math.max(currentPage - 2, 1);
  let endLink = startLink + maxLinks - 1;
  if (endLink > totalPages) {
    endLink = totalPages;
    startLink = Math.max(endLink - (maxLinks - 1), 1);
  }
  const pageNumbersToShow = [];
  for (let p = startLink; p <= endLink; p++) {
    pageNumbersToShow.push(p);
  }

  return (
    <div>
      <Button variant="secondary" onClick={handleBack}>Back</Button>
      <Container className="mt-5">
        <h1 className="text-center mb-4">Ledger Report</h1>

        {/* Filters */}
        <Row className="mb-4">
          <Col md={scope.isAdmin ? 3 : 4}>
            <Select
              options={accountOptions}
              value={selectedAccount}
              onChange={handleAccountChange}
              placeholder="Select Account"
              isSearchable
            />
          </Col>
          <Col md={scope.isAdmin ? 3 : 4}>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => handleDateChange("start", e.target.value)}
            />
          </Col>
          <Col md={scope.isAdmin ? 3 : 4}>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => handleDateChange("end", e.target.value)}
            />
          </Col>
          {scope.isAdmin && (
              <Col md={3}>
                <AccountingScopeSelect scope={scope}/>
              </Col>
          )}
        </Row>

        <Row className="mb-4">
          <Col className="d-flex gap-2 justify-content-end">
            <Button
              variant="outline-primary"
              disabled={!selectedAccount || exportingPdf}
              onClick={() => handleExportPdf("filtered")}
            >
              {exportingPdf ? "Preparing PDF..." : "Export Filtered PDF"}
            </Button>
            <Button
              variant="outline-dark"
              disabled={!selectedAccount || exportingPdf}
              onClick={() => handleExportPdf("all_to_date")}
            >
              Export All To Date PDF
            </Button>
          </Col>
        </Row>

        <Card>
          <Card.Header>
            Ledger for {selectedAccount?.label || "..."} - {scope.scopeLabel}
          </Card.Header>
          <Card.Body>
            {(!selectedAccount) && (
              <p>Please select an account to view its ledger.</p>
            )}

            {selectedAccount && ledgerTransactions.length === 0 && (
              <p>No transactions found for this account/date range.</p>
            )}

            {selectedAccount && ledgerTransactions.length > 0 && (
              <>
                <div className="mb-3">
                    <strong>Opening Balance (Page {currentPage}):</strong>
                    {formatDrCr(openingBalanceForPage, accountTypeName)}
                </div>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Transaction #</th>
                      <th>Date & Time</th>
                      <th>Reference</th>
                      <th>Description</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Balance</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processedTransactions.map(trx => (
                      <tr key={trx.id}>
                        <td>
                          <Button
                            variant="link"
                            className="p-0"
                            onClick={() => handleShowDetails(trx)}
                          >
                            {trx.transaction_number}
                          </Button>
                        </td>
                        <td>
                          {/*{new Date(trx.transaction_date).toLocaleDateString("en-GB")}*/}
                          {formatNairobiDateTime(trx.created_at || trx.transaction_date)}
                        </td>
                        <td>{trx.reference}</td>
                        <td>{trx.description}</td>
                        <td>{trx.totalDebit.toFixed(2)}</td>
                        <td>{trx.totalCredit.toFixed(2)}</td>
                        {/*<td>{trx.balance.toFixed(2)}</td>*/}
                        <td>{formatDrCr(trx.balance, accountTypeName)}</td>
                        <td>
                          <Button variant="info" onClick={() => handleShowDetails(trx)}>
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {totalPages > 1 && (
              <Pagination className="justify-content-center mt-4">
                <Pagination.First
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                {/*{Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (*/}
                {pageNumbersToShow.map((pg) => (
                  <Pagination.Item
                    key={pg}
                    active={pg === currentPage}
                    onClick={() => handlePageChange(pg)}
                  >
                    {pg}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Details modal */}
      <Modal show={showDetails} onHide={handleCloseDetails} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Transaction Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTransaction && (
            <>
              <h5>Transaction #: {selectedTransaction.transaction_number}</h5>
              <h5>
                {/*Date: {new Date(selectedTransaction.transaction_date).toLocaleDateString("en-GB")}*/}
                Date & Time: {formatNairobiDateTime(selectedTransaction.created_at || selectedTransaction.transaction_date)}
              </h5>
              <h5>Reference: {selectedTransaction.reference}</h5>
              <h5>Description: {selectedTransaction.description}</h5>

              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Particulars</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTransaction.items.map((it, i2) => (
                    <tr key={i2}>
                      <td>{it.account_details?.account_name}</td>
                      <td>{it.item_description}</td>
                      <td>{it.transaction_type === "DEBIT" ? it.amount : ""}</td>
                      <td>{it.transaction_type === "CREDIT" ? it.amount : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LedgerReports;
